/*
 * The per-product configuration form (U3 — slimmed).
 *
 * Mirrors the `ProductConfigPayload`: FIVE sections —
 *   1. Identity & targeting (product key, Notion product, data source, ad account)
 *   2. Master sheet (sheet id, tab, data start row)
 *   3. Naming (a single AD_NAMING column letter — KTD-5)
 *   4. Scheduled conversion config (pixel id, prefilled goal/destination/event,
 *      a single geo country)
 *   5. Launch defaults (default headline, UTM template, ensure-body-link toggle — U6)
 *
 * The Default-persona, Routing-rules, Behavior, and POV-classification sections
 * (and the column-map / anchor-headers editors) were removed wholesale. Holds an
 * editable draft; validates inline against the backend rules (validation.ts) and
 * gates Save; serializes via serialize.ts.
 *
 * Writes are gated to operator+ (`configure`); a viewer sees the same form in
 * read-only mode. Server errors (409 cross-brand account/campaign conflict,
 * 422 malformed/unauthorized account) surface through an Alert.
 */

import { useMemo, useState } from "react";
import { Alert, Button, Checkbox, Group, Select, SimpleGrid, Stack, Text, TextInput } from "@mantine/core";
import { errorMessage } from "../../api/errors";
import { ApiError } from "../../api/client";
import { sendDigestTest } from "../../api/endpoints";
import { Section } from "./Section";
import { toDraft, type ProductDraft } from "./serialize";
import {
  validateDraft,
  isValidAccountId,
  isValidAdNamingColumn,
  isValidCurrency,
  isValidDefaultLink,
  isValidDefaultPageId,
  isValidGeoCountry,
} from "./validation";
import type { ProductConfigResponse } from "./hooks";
import styles from "./config.module.css";

/** Turn a server error into a section-appropriate, human message (KTD 11). */
function saveErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409) {
      return (
        "That ad account or target campaign is already bound to another " +
        "brand. Each Meta ad account belongs to exactly one brand — pick " +
        "an account this brand owns."
      );
    }
    if (err.status === 403) {
      return "You don't have permission to save product configuration.";
    }
    // 422 carries the field-level reason (malformed / unauthorized account).
    if (err.status === 422) return errorMessage(err);
  }
  return errorMessage(err);
}

/**
 * Send this product's digest now, as a test (2026-10-30), to the SAVED member
 * ID: proves the ID, the bot's right to DM and the stuck list in one click.
 */
function DigestTestButton({ productKey }: { productKey: string }) {
  const [state, setState] = useState<{ busy: boolean; ok: string | null; error: string | null }>({
    busy: false,
    ok: null,
    error: null,
  });
  async function run() {
    setState({ busy: true, ok: null, error: null });
    try {
      const r = await sendDigestTest(productKey);
      setState({
        busy: false,
        ok: `Sent to ${r.recipient}: ${r.stuck === 0 ? "nothing stuck right now" : `${r.stuck} stuck ticket${r.stuck === 1 ? "" : "s"}`}.`,
        error: null,
      });
    } catch (err) {
      setState({ busy: false, ok: null, error: errorMessage(err) });
    }
  }
  return (
    <Stack gap={4}>
      <Group>
        <Button variant="default" size="xs" loading={state.busy} onClick={run}>
          Send test DM
        </Button>
      </Group>
      {state.ok ? <Text fz="xs" c="teal">{state.ok}</Text> : null}
      {state.error ? <Text fz="xs" c="red">{state.error}</Text> : null}
    </Stack>
  );
}

export function ProductConfigForm({
  product,
  productKey,
  canEdit,
  saving,
  saveError,
  onSave,
}: {
  /** The existing config, or undefined for a brand-new product. */
  product: ProductConfigResponse | undefined;
  /** The product key (fixed for an existing product; entered for a new one). */
  productKey: string;
  canEdit: boolean;
  saving: boolean;
  saveError: unknown;
  onSave: (args: { key: string; config: ProductDraft }) => void;
}) {
  const readOnly = !canEdit;
  // Re-seed the draft whenever the selected product changes (key is the identity).
  const initial = useMemo(() => toDraft(product?.config), [product]);
  const [draft, setDraft] = useState<ProductDraft>(initial);
  const [keyDraft, setKeyDraft] = useState(productKey);
  const [submitted, setSubmitted] = useState(false);
  const isNew = product === undefined;

  // Reset local state when the product identity changes (picker switch).
  const seedKey = product ? product.product_key : `__new__:${productKey}`;
  const [seed, setSeed] = useState(seedKey);
  if (seed !== seedKey) {
    setSeed(seedKey);
    setDraft(initial);
    setKeyDraft(productKey);
    setSubmitted(false);
  }

  const errors = useMemo(() => validateDraft(draft), [draft]);
  // A key is always required — for a new product AND when renaming an existing one.
  const keyMissing = !keyDraft.trim();
  const blocked = !errors.ok || keyMissing;

  function set<K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  function handleSave() {
    setSubmitted(true);
    if (blocked) return;
    onSave({
      // Always the editable draft — for a new product this is its key; for an
      // existing one, a changed value renames it.
      key: keyDraft.trim(),
      config: draft,
    });
  }

  // Surfaced only after a save attempt to avoid noisy first-paint errors.
  const showErrors = submitted;

  const accountInvalid =
    Boolean(draft.account_id.trim()) && !isValidAccountId(draft.account_id);
  const funnelsInvalid = draft.funnelflux_funnel_ids
    .split(/[\s,]+/)
    .some((s) => s.trim() !== "" && !/^\d{15,22}$/.test(s.trim()));
  const digestUserInvalid =
    draft.digest_slack_user_id.trim() !== "" && !/^[UW][A-Z0-9]{6,12}$/.test(draft.digest_slack_user_id.trim());
  const geoInvalid =
    Boolean(draft.geo_country.trim()) && !isValidGeoCountry(draft.geo_country);
  const defaultLinkInvalid =
    Boolean(draft.default_link.trim()) && !isValidDefaultLink(draft.default_link);
  const defaultPageIdInvalid =
    Boolean(draft.default_page_id.trim()) && !isValidDefaultPageId(draft.default_page_id);
  const adNamingInvalid =
    showErrors && !isValidAdNamingColumn(draft.ad_naming_column);
  const currencyInvalid =
    Boolean(draft.currency.trim()) && !isValidCurrency(draft.currency);

  return (
    <Stack gap="lg" style={{ minWidth: 0 }}>
      {saveError != null && (
        <Alert color="red" variant="light" role="alert">
          {saveErrorMessage(saveError)}
        </Alert>
      )}

      <Section
        title="Identity & targeting"
        description="The Notion product, data source, and the Meta ad account this product launches into."
      >
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <TextInput
            label="Product key"
            required
            description={
              isNew
                ? "Unique across all brands; used in URLs and the run queue."
                : "Editing renames the product. Must stay unique across all brands."
            }
            error={showErrors && keyMissing ? "A product key is required." : undefined}
            value={keyDraft}
            onChange={(e) => setKeyDraft(e.currentTarget.value)}
            placeholder="e.g. acme"
            readOnly={readOnly}
            disabled={readOnly}
          />
          <TextInput
            label="Notion product"
            description="The Brand select value used to filter the data source."
            value={draft.notion_product}
            readOnly={readOnly}
            onChange={(e) => set("notion_product", e.currentTarget.value)}
          />
          <TextInput
            label="Notion data source id"
            value={draft.notion_data_source_id}
            readOnly={readOnly}
            onChange={(e) => set("notion_data_source_id", e.currentTarget.value)}
          />
          <TextInput
            label="Ad account id"
            required
            description="Bare number — `act_` is added automatically."
            error={accountInvalid ? errors.fields.account_id : undefined}
            value={draft.account_id}
            readOnly={readOnly}
            onChange={(e) => set("account_id", e.currentTarget.value)}
            placeholder="1234567890"
          />
          <TextInput
            label="FunnelFlux funnel ids (optional)"
            description="This product's Facebook funnels, comma-separated. Creative Analysis takes revenue per ad from these; spend stays Meta."
            error={funnelsInvalid ? "Funnel ids are long numbers, e.g. 2001169224829472199" : undefined}
            value={draft.funnelflux_funnel_ids}
            readOnly={readOnly}
            onChange={(e) => set("funnelflux_funnel_ids", e.currentTarget.value)}
            placeholder="2001169224829472199"
          />
          <TextInput
            label="Stuck-ticket digest: Slack member ID (optional)"
            description="This product's media buyer. They get a daily Slack DM of this product's delivered video tickets stuck 2+ days. In Slack: their profile, then ⋯, then Copy member ID."
            error={digestUserInvalid ? "A Slack member ID looks like U05ABC123DE" : undefined}
            value={draft.digest_slack_user_id}
            readOnly={readOnly}
            onChange={(e) => set("digest_slack_user_id", e.currentTarget.value)}
            placeholder="U05ABC123DE"
          />
          {!isNew && canEdit && product?.config?.digest_slack_user_id ? (
            <DigestTestButton productKey={product.product_key} />
          ) : null}
        </SimpleGrid>
      </Section>

      <Section
        title="Master sheet"
        description="The product's Google Sheet locator for copy/concept lookup."
      >
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <TextInput
            label="Sheet id"
            value={draft.sheet_id}
            readOnly={readOnly}
            onChange={(e) => set("sheet_id", e.currentTarget.value)}
          />
          <TextInput
            label="Tab name"
            description="The sheet's tab NAME exactly as shown in Google Sheets (e.g. “Launch Log Book”) — not the tab index or gid."
            value={draft.sheet_tab}
            readOnly={readOnly}
            placeholder="e.g. Launch Log Book"
            onChange={(e) => set("sheet_tab", e.currentTarget.value)}
          />
          <TextInput
            label="Data start row"
            description="First row of data (header rows above)."
            type="number"
            min={1}
            value={draft.data_start_row}
            readOnly={readOnly}
            onChange={(e) => set("data_start_row", e.currentTarget.value)}
          />
        </SimpleGrid>
      </Section>

      <Section
        title="Naming"
        description="The spreadsheet column letter that holds the AD_NAMING string (e.g. V), and reporting display options."
      >
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <TextInput
            label="AD_NAMING column"
            required
            description="A single column letter, e.g. V."
            error={adNamingInvalid ? errors.fields.ad_naming_column : undefined}
            value={draft.ad_naming_column}
            readOnly={readOnly}
            onChange={(e) => set("ad_naming_column", e.currentTarget.value)}
            placeholder="V"
          />
          <TextInput
            label="Display currency"
            description="Symbol shown on Creative Analysis, e.g. £. Blank = $."
            error={currencyInvalid ? errors.fields.currency : undefined}
            value={draft.currency}
            readOnly={readOnly}
            onChange={(e) => set("currency", e.currentTarget.value)}
            placeholder="$"
          />
        </SimpleGrid>
      </Section>

      <Section
        title="Scheduled conversion config"
        description="Conversion settings for scheduled launches. The goal / destination / event are prefilled with editable defaults."
      >
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <TextInput
            label="Pixel id"
            value={draft.pixel_id}
            readOnly={readOnly}
            onChange={(e) => set("pixel_id", e.currentTarget.value)}
          />
          <TextInput
            label="Optimisation goal"
            value={draft.optimisation_goal}
            readOnly={readOnly}
            onChange={(e) => set("optimisation_goal", e.currentTarget.value)}
            placeholder="e.g. OFFSITE_CONVERSIONS"
          />
          <TextInput
            label="Destination"
            value={draft.destination}
            readOnly={readOnly}
            onChange={(e) => set("destination", e.currentTarget.value)}
            placeholder="e.g. WEBSITE"
          />
          <TextInput
            label="Event"
            value={draft.event}
            readOnly={readOnly}
            onChange={(e) => set("event", e.currentTarget.value)}
            placeholder="e.g. PURCHASE"
          />
          <TextInput
            label="Geo country"
            description="ISO-3166 alpha-2 code (uppercased), e.g. US. The United Kingdom is GB (a saved UK is auto-corrected)."
            error={geoInvalid ? errors.fields.geo_country : undefined}
            value={draft.geo_country}
            readOnly={readOnly}
            onChange={(e) => set("geo_country", e.currentTarget.value)}
            placeholder="US"
          />
          <TextInput
            label="Exclusion audience id(s)"
            description="Custom-audience id(s) excluded when scheduled adsets are created — comma-separated for multiple (e.g. the legal exclusion + Purchase L180D). Find an id: Ads Manager → Audiences → click the audience; the id is in the detail panel / URL. Empty = no exclusion."
            value={draft.exclusion_audience_id}
            readOnly={readOnly}
            onChange={(e) => set("exclusion_audience_id", e.currentTarget.value)}
            placeholder="e.g. 120211234567890123, 120219876543210987"
          />
          <TextInput
            label="Bid strategy"
            description="Adset bid strategy for scheduled (ABO) launches, e.g. COST_CAP or LOWEST_COST_WITH_BID_CAP. Empty = inherit the campaign's strategy (CBO behaviour)."
            value={draft.bid_strategy}
            readOnly={readOnly}
            onChange={(e) => set("bid_strategy", e.currentTarget.value)}
            placeholder="e.g. COST_CAP"
          />
          <TextInput
            label="Bid / cost-cap amount"
            description="In the ad account's currency (e.g. 55 = $55.00). Required when the campaign or the strategy above uses a cost/bid cap. Verify the first scheduled adset's bid in Ads Manager after changing this."
            value={draft.bid_amount}
            readOnly={readOnly}
            onChange={(e) => set("bid_amount", e.currentTarget.value)}
            placeholder="e.g. 55"
          />
          <Select
            label="Attribution setting"
            description="Applied to scheduled-created adsets (created adsets inherit NOTHING — without this Meta uses its account default). Verify the first adset in Ads Manager after changing."
            data={[
              { value: "", label: "Meta default (7-day click, 1-day view)" },
              { value: "1d_click", label: "1-day click" },
              { value: "7d_click", label: "7-day click" },
              { value: "1d_click_1d_view", label: "1-day click, 1-day view" },
              { value: "7d_click_1d_view", label: "7-day click, 1-day view" },
            ]}
            value={draft.attribution_setting}
            disabled={readOnly}
            onChange={(v) => set("attribution_setting", v ?? "")}
            comboboxProps={{ withinPortal: true }}
          />
          <TextInput
            label="Billing event"
            description="For scheduled-created adsets, e.g. IMPRESSIONS. Empty = omitted (Meta default)."
            value={draft.billing_event}
            readOnly={readOnly}
            onChange={(e) => set("billing_event", e.currentTarget.value)}
            placeholder="e.g. IMPRESSIONS"
          />
          <TextInput
            label="Daily budget"
            description="Adset daily budget in the account's currency — ABO campaigns only (CBO campaigns reject adset budgets). Empty = omitted."
            value={draft.daily_budget}
            readOnly={readOnly}
            onChange={(e) => set("daily_budget", e.currentTarget.value)}
            placeholder="e.g. 100"
          />
          <TextInput
            label="Advanced create fields (JSON)"
            description="Extra create-adset fields merged into scheduled creates, e.g. {&quot;useAcceleratedDelivery&quot;: false}. Identity/targeting keys are ignored. Empty = none."
            value={draft.create_extras}
            readOnly={readOnly}
            onChange={(e) => set("create_extras", e.currentTarget.value)}
            placeholder='{"fieldName": "value"}'
          />
        </SimpleGrid>
      </Section>

      <Section
        title="Launch defaults"
        description="Per-product launch hygiene: a fallback headline, the UTM template applied to destination links, and whether a plain link is inserted into ad copy that has none."
      >
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <TextInput
            label="Default headline"
            description="Used when the ticket has no headline (leaves the ticket not-launchable if blank)."
            value={draft.default_headline}
            readOnly={readOnly}
            onChange={(e) => set("default_headline", e.currentTarget.value)}
            placeholder="e.g. Feel the difference"
          />
          <TextInput
            label="UTM template"
            description="Applied to the destination link's tracking params; falls back to the global default when blank."
            value={draft.utm_template}
            readOnly={readOnly}
            onChange={(e) => set("utm_template", e.currentTarget.value)}
            placeholder="utm_source=fb&utm_campaign={{campaign.id}}"
          />
          <TextInput
            label="Default landing URL"
            description="Always prefills the wizard's URL field for this product (overrides whatever link the last run used); editable per launch."
            error={defaultLinkInvalid ? errors.fields.default_link : undefined}
            value={draft.default_link}
            readOnly={readOnly}
            onChange={(e) => set("default_link", e.currentTarget.value)}
            placeholder="https://shop.example/product"
          />
          <TextInput
            label="Default page id"
            description="Prefills the wizard's Page pick when the product has no prior run to copy from."
            error={defaultPageIdInvalid ? errors.fields.default_page_id : undefined}
            value={draft.default_page_id}
            readOnly={readOnly}
            onChange={(e) => set("default_page_id", e.currentTarget.value)}
            placeholder="1234567890"
          />
        </SimpleGrid>
        <Checkbox
          mt="md"
          label="Insert link in ad copy if missing"
          checked={draft.ensure_body_link}
          readOnly={readOnly}
          disabled={readOnly}
          onChange={(e) => set("ensure_body_link", e.currentTarget.checked)}
        />
        <Checkbox
          mt="sm"
          label="Paused (account not running)"
          description="Ticket hygiene stops checking this product's tickets and leaves it out of the Slack digest. Launching still works."
          checked={draft.paused}
          readOnly={readOnly}
          disabled={readOnly}
          onChange={(e) => set("paused", e.currentTarget.checked)}
        />
      </Section>

      {canEdit && (
        <Group justify="space-between" wrap="wrap" gap="md" className={styles.footer}>
          <Text fz="sm" c="dimmed">
            {showErrors && blocked
              ? "Fix the highlighted fields before saving."
              : isNew
                ? "New product — fill the key and save to create."
                : "Edit and save to update this product."}
          </Text>
          <Button
            type="button"
            loading={saving}
            disabled={showErrors && blocked}
            onClick={handleSave}
          >
            {isNew ? "Create product" : "Save changes"}
          </Button>
        </Group>
      )}
    </Stack>
  );
}

export type { ProductDraft } from "./serialize";
